import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

import coreUtils from '../../utils/coreUtils';
import { STATUS_TO_ICON, BUILD_STATUS_TO_COLOR } from '../../config/constants';

// Styled components from TestScenariosList
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderBottom: '1px solid var(--borderColor)',
  color: 'var(--text-primary)',
  '&.checkbox-cell': {
    width: '32px',
    padding: theme.spacing(0.5),
  },
  '&.date-cell': {
    width: '140px',
    padding: theme.spacing(1, 1.5),
    whiteSpace: 'nowrap',
  },
  '&.action-cell': {
    width: '48px',
    padding: theme.spacing(0.5),
  }
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  backgroundColor: 'var(--background-primary)',
  fontWeight: 600,
  fontSize: '1rem',
  color: 'var(--text-primary)',
  borderBottom: '2px solid var(--borderColor)',
  '&.checkbox-cell': {
    width: '32px',
    padding: theme.spacing(0.5),
  },
  '&.date-cell': {
    width: '140px',
    padding: theme.spacing(1, 1.5),
  },
  '&.action-cell': {
    width: '48px',
    padding: theme.spacing(0.5),
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    active: theme.palette.success.main,
    draft: theme.palette.warning.main,
    archived: 'var(--text-tertiary)',
    failed: theme.palette.error.main,
    running: theme.palette.info.main,
    paused: theme.palette.warning.main,
  };
  return {
    color: colors[status?.toLowerCase()] || 'var(--text-tertiary)',
    backgroundColor: 'transparent',
    borderColor: colors[status?.toLowerCase()] || 'var(--text-tertiary)',
    '& .MuiChip-label': {
      fontWeight: 500,
    },
  };
});

const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'var(--background-secondary)',
  color: 'var(--text-primary)',
  borderRadius: theme.shape.borderRadius,
  height: 20,
  '& .MuiChip-label': {
    fontSize: '0.75rem',
    padding: '4px 8px',
  },
}));

const TestPlanListTable = ({
  testPlanList = [],
  onViewTestPlan = () => {},
  onClickDelete = () => {},
  showRunningStatusDialog = () => {},
  onBuild = () => {},
  onViewBuilds = () => {},
  onMarkActive = () => {}
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId, setMenuId] = useState(null);

  const styles = {
    paper: {
      backgroundColor: 'var(--background-primary)',
      color: 'var(--text-primary)',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: 'var(--hover-bg)',
      },
      '&:last-child td, &:last-child th': {
        borderBottom: '1px solid var(--borderColor)',
      },
    },
    description: {
      color: 'var(--text-secondary)',
    },
    menu: {
      '& .MuiPaper-root': {
        backgroundColor: 'var(--background-primary)',
        color: 'var(--text-primary)',
        border: '1px solid var(--borderColor)',
      },
      '& .MuiMenuItem-root': {
        fontSize: '0.95rem',
        padding: '8px 16px',
        color: 'var(--text-primary)',
        '&:hover': {
          backgroundColor: 'var(--hover-bg)',
        },
      },
    },
    iconButton: {
      color: 'var(--text-secondary)',
      '&:hover': {
        backgroundColor: 'var(--hover-bg)',
      },
    },
  };

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuId(null);
  };

  if (!testPlanList.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" sx={{ color: 'var(--text-secondary)' }}>
          No results found.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={styles.paper}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>Details</StyledTableHeaderCell>
              <StyledTableHeaderCell>Environment</StyledTableHeaderCell>
              <StyledTableHeaderCell>Status</StyledTableHeaderCell>
              <StyledTableHeaderCell>Frequency</StyledTableHeaderCell>
              <StyledTableHeaderCell>Time Gap</StyledTableHeaderCell>
              <StyledTableHeaderCell>Last Run Result</StyledTableHeaderCell>
              <StyledTableHeaderCell className="action-cell" align="center">Action</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testPlanList.map(({
              id,
              code,
              title,
              description,
              status,
              frequency,
              time_gap,
              paused,
              last_build_status,
              last_executed_at,
              environment_set
            }) => (
              <TableRow
                key={id}
                hover
                sx={{
                  ...styles.tableRow,
                  backgroundColor: "var(--background-primary)"
                }}
              >
                <StyledTableCell
                  onClick={() => onViewTestPlan(id)}
                  sx={{ cursor: 'pointer', width: '300px' }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          marginRight: 1,
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          minWidth: 'fit-content'
                        }}
                      >
                        {code}:
                      </Box>
                      {title}
                    </Typography>
                    {description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {description}
                      </Typography>
                    )}
                  </Box>
                </StyledTableCell>

                <StyledTableCell>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {environment_set?.title || '-'}
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <StatusChip
                    label={status}
                    status={status}
                    variant="outlined"
                    size="small"
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {frequency}
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {(() => {
                      const timeObj = coreUtils.parseMinutes(time_gap) || {};
                      let arr = [];
                      Object.keys(timeObj).map(k => {
                        arr.push(timeObj[k] < 10 ? ("0" + timeObj[k]) : timeObj[k]);
                      });
                      return arr.join(" : ");
                    })()}
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <Stack spacing={0.5}>
                    {last_build_status && (
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box component="span" sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {environment_set?.title || '-'}
                        </Box>
                        :
                        <Box
                          component="span"
                          sx={{ color: BUILD_STATUS_TO_COLOR[last_build_status] || 'var(--text-secondary)' }}
                        >
                          {last_executed_at ? dayjs(last_executed_at * 1000).fromNow() : last_build_status || '-'}
                        </Box>
                      </Typography>
                    )}
                  </Stack>
                </StyledTableCell>

                <StyledTableCell className="action-cell" align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, id)}
                    sx={styles.iconButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={styles.menu}
      >
        {(() => {
          const status = testPlanList.find(t => t.id === menuId)?.status;
          const paused = testPlanList.find(t => t.id === menuId)?.paused;
          return status === 'active' && (
            <>
              <MenuItem onClick={() => {
                onBuild(menuId);
                handleMenuClose();
              }}>
                Run
              </MenuItem>
              <MenuItem onClick={() => {
                showRunningStatusDialog({ id: menuId, paused });
                handleMenuClose();
              }}>
                Pause
              </MenuItem>
            </>
          );
        })()}
        <MenuItem onClick={() => {
          onViewBuilds(menuId);
          handleMenuClose();
        }}>
          All builds
        </MenuItem>
        {(() => {
          const status = testPlanList.find(t => t.id === menuId)?.status;
          return (status === 'draft' || status === 'paused' || status === 'archived') && (
            <MenuItem onClick={() => {
              onMarkActive(menuId);
              handleMenuClose();
            }}>
              Mark as Active
            </MenuItem>
          );
        })()}
        <MenuItem
          onClick={() => {
            onClickDelete(menuId);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default TestPlanListTable;